/**
 * Thin API client for the Eclipse service.
 *
 * Attaches the Cognito ID token to every request, handles 401 by
 * attempting a silent token refresh before giving up, and normalizes error
 * responses into a single exception class the UI can pattern-match on.
 *
 * Note: the API Gateway's Cognito authorizer validates the ID token, not the
 * access token. We send the ID token (falling back to the access token only if
 * the ID token is unavailable) — matching the Portal's apiClient.
 */

import { getIdToken, getAccessToken, refreshTokensAsync, clearTokens, redirectToLogin } from "./authService"

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "https://api.eclipse.beta.freshfoodproduction.wwgs.amazon.dev"

export interface ApiRequestOptions {
  method?: string
  body?: unknown
  headers?: Record<string, string>
  timeoutMs?: number
}

export class ApiError extends Error {
  readonly status: number
  readonly body?: unknown

  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.body = body
  }
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
  // Internal: how many silent-refresh retries remain. Caps recursion so a
  // request that keeps 401ing despite a "successful" refresh (e.g. an authz
  // failure or clock skew) cannot loop forever and hammer the API.
  retriesLeft = 1,
): Promise<T> {
  const { method = "GET", body, headers = {}, timeoutMs = 15_000 } = options

  const skipAuth = (import.meta.env.VITE_SKIP_AUTH as string | undefined) === "true"
  // The API Gateway Cognito authorizer validates the ID token (not the access
  // token), so prefer the ID token — matching the Portal's apiClient.
  let token = getIdToken() || getAccessToken()
  if (!skipAuth && !token) {
    // No usable token — route to the Midway/Cognito hosted login (matches the
    // Portal). redirectToLogin() sets window.location.href, which is idempotent
    // across simultaneous calls, so this can't cause a redirect loop.
    redirectToLogin()
    throw new ApiError(401, "Session expired. Please sign in again.")
  }

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    })

    if (!skipAuth && response.status === 401) {
      // Attempt silent token refresh before giving up — but only while retries
      // remain, so a persistently-401ing request cannot recurse indefinitely.
      const refreshed = retriesLeft > 0 && (await refreshTokensAsync())
      if (refreshed) {
        // Retry the original request once. refreshTokensAsync() has stored the
        // new tokens, so the recursive call re-reads the fresh ID token itself
        // (don't override Authorization with the access token here).
        clearTimeout(timer)
        return apiRequest<T>(path, options, retriesLeft - 1)
      }
      // Refresh failed — clear the stale session and route to the Midway/Cognito
      // hosted login (F10: expired creds must redirect to login everywhere, not
      // surface a raw "session expired" error on a data screen like Consume
      // Materials). Matches the Portal's apiClient behaviour.
      clearTokens()
      redirectToLogin()
      throw new ApiError(401, "Session expired. Please sign in again.")
    }

    const text = await response.text()
    const parsed = text ? safeJsonParse(text) : undefined

    if (!response.ok) {
      const userMessage = formatApiErrorMessage(response.status, parsed)
      throw new ApiError(response.status, userMessage, parsed ?? text)
    }

    return parsed as T
  } finally {
    clearTimeout(timer)
  }
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

/**
 * Format API error into a user-friendly message for the ApiError.message field.
 * The friendlyErrorMessage() utility in utils/errors.ts provides further mapping,
 * but this ensures even direct .message access gives reasonable text.
 */
function formatApiErrorMessage(status: number, body: unknown): string {
  // Try to extract server message
  let serverMessage: string | null = null
  if (typeof body === "object" && body && "message" in body) {
    const msg = (body as { message?: unknown }).message
    if (typeof msg === "string" && msg.length > 0) {
      serverMessage = msg
    }
  }

  switch (status) {
    case 400:
      // Smithy validation errors — simplify for users
      if (serverMessage && serverMessage.includes("validation errors detected")) {
        return "Invalid input. Please check your entries."
      }
      return serverMessage || "Invalid input. Please check your entries."
    case 404: return serverMessage || "Not found."
    case 503: return "Service temporarily unavailable. Please try again in a moment."
    case 500: return serverMessage || "Something went wrong. Please try again."
    default: return serverMessage || `Unexpected error (${status}).`
  }
}
